import { createApiClient, MessengerClient, type ApiClient, type Platform, type PlatformAppInfo, type ServerEntry } from '@sd/client-core';
import { api, type Me, type ServerInfo } from '@sd/contracts';
import type { UpdateInfo } from '@sd/contracts/ipc';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AppActionsContext, AppEnvContext, ClientContext, useClientState, type AppActions, type AppEnv, type ModalState } from '../messenger/context';
import type { MobileView } from '../messenger/layout';
import { MessengerScreen } from '../messenger/MessengerScreen';
import { ConnectScreen } from '../screens/connect/ConnectScreen';
import type { ConnectedServer } from '../screens/types';
import { Icon } from '../icons/Icon';
import { AvatarSourceContext } from '../ui/Avatar';
import { Background, Button } from '../ui/controls';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { UiProvider, useConfirm, useToast } from '../ui/overlays';
import { useTheme } from '../ui/theme';
import { useRouter, type Route } from './router';

export interface MessengerAppProps {
  platform: Platform;
  /** Dev/test: builds clients (fixture mode). Defaults to real HTTP + Socket.IO clients. */
  clientFactory?: (serverId: string) => MessengerClient;
  /** Dev/test: fixed clock for relative dates. */
  now?: () => Date;
  /** Dev/test: first panel on phones and `?posture=` simulation. */
  initialView?: MobileView;
  allowPostureParam?: boolean;
  /** Dev/test: initial route instead of the URL. */
  initialRoute?: Route;
}

/** Root of the shared client (web and desktop renderer). */
export function MessengerApp(props: MessengerAppProps) {
  return (
    <UiProvider>
      <Background />
      <div id="app">
        <ErrorBoundary
          fallback={(reset) => (
            <Centered>
              <QuitWhenAsked platform={props.platform} />
              <Icon name="alertc" size="lg" />
              <b>화면을 표시하지 못했어요</b>
              <span style={{ color: 'var(--ink-2)', fontSize: 14 }}>작성 중인 메시지와 대기 중인 메시지는 그대로 있어요.</span>
              <Button variant="primary" onClick={reset}>
                다시 시도
              </Button>
            </Centered>
          )}
        >
          <AppRoot {...props} />
        </ErrorBoundary>
      </div>
    </UiProvider>
  );
}

type SessionStatus = 'idle' | 'starting' | 'ready' | 'signed_out' | 'error';

const SIGNED_OUT_TEXT: Record<string, string> = {
  revoked: '다른 기기에서 이 세션을 로그아웃했어요. 다시 로그인해 주세요',
  deactivated: '계정이 비활성화됐어요. 회사 관리자에게 문의해 주세요',
  expired: '로그인이 만료됐어요. 다시 로그인해 주세요',
  logged_out: '로그아웃했어요',
};

/**
 * What these sessions hold that the server does not have yet: sends it has not confirmed, drafts
 * and files in composer trays, as the confirmation before quitting names them; null when nothing.
 */
function unsentSummary(clients: MessengerClient[]): string | null {
  let messages = 0;
  let drafts = 0;
  let files = 0;
  for (const { state } of clients) {
    messages += Object.keys(state.pending).length;
    drafts += Object.values(state.drafts).filter((text) => text.trim()).length;
    files += Object.values(state.trays).reduce((n, items) => n + items.length, 0);
  }
  const parts = [messages ? `보내지 않은 메시지 ${messages}개` : '', drafts ? `임시저장 ${drafts}개` : '', files ? `첨부하려던 파일 ${files}개` : ''].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

/** Why what `unsentSummary` names would be lost; the last sentence of every confirmation before the app restarts or quits. */
const NOT_KEPT = '이 PC에서 안전한 비밀 저장소를 찾지 못해 저장해 둘 수 없어요.';

/**
 * While the app cannot show (it failed to render), a quit the shell asks for (`app.quitRequested`)
 * goes ahead: no session is reachable to save or count, and the app must not stay unquittable.
 */
function QuitWhenAsked({ platform }: { platform: Platform }) {
  useEffect(
    () =>
      platform.onEvent((event) => {
        if (event.type === 'app.quitRequested') void platform.quit?.().catch(() => {});
      }),
    [platform],
  );
  return null;
}

// Pages most visits never show come when first opened, so a phone gets to the conversations sooner.
const AdminScreen = lazy(() => import('../screens/admin/AdminScreen').then((m) => ({ default: m.AdminScreen })));
const DownloadPage = lazy(() => import('../screens/download/DownloadPage').then((m) => ({ default: m.DownloadPage })));
const InviteAcceptScreen = lazy(() => import('../screens/invite/InviteAcceptScreen').then((m) => ({ default: m.InviteAcceptScreen })));
const InviteModal = lazy(() => import('../messenger/modals/InviteModal').then((m) => ({ default: m.InviteModal })));
const PasswordResetScreen = lazy(() => import('../screens/auth/PasswordResetScreen').then((m) => ({ default: m.PasswordResetScreen })));
const SetupScreen = lazy(() => import('../screens/setup/SetupScreen').then((m) => ({ default: m.SetupScreen })));

/** A page that loads when first opened, with the loading screen meanwhile. */
function LazyPage({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<Loading text="불러오는 중…" />}>
      {children}
    </Suspense>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <section className="screen on scrolly" aria-live="polite">
      <div className="page" style={{ display: 'grid', placeItems: 'center' }}>
        <div className="glass" style={{ padding: 28, borderRadius: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center', maxWidth: 420 }}>
          {children}
        </div>
      </div>
    </section>
  );
}

function Loading({ text }: { text: string }) {
  return (
    <Centered>
      <Icon name="sync" className="spin" />
      <span>{text}</span>
    </Centered>
  );
}

function AppRoot({ platform, clientFactory, now: nowProp, initialView, allowPostureParam = false, initialRoute }: MessengerAppProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const theme = useTheme();
  const { route, navigate } = useRouter(platform.kind, initialRoute);
  const now = useMemo(() => nowProp ?? (() => new Date()), [nowProp]);
  const [appInfo, setAppInfo] = useState<PlatformAppInfo | null>(null);
  /** The host could not describe itself; counting attempts lets "다시 시도" run the boot again. */
  const [boot, setBoot] = useState({ attempt: 0, failed: false });
  const [servers, setServers] = useState<ServerEntry[] | null>(null);
  const [activeServerId, setActiveServerId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Record<string, SessionStatus>>({});
  const [serverUnread, setServerUnread] = useState<Record<string, number>>({});
  const [modal, setModal] = useState<ModalState>(null);
  const [publicInfo, setPublicInfo] = useState<ServerInfo | null>(null);
  const [update, setUpdate] = useState<UpdateInfo | null>(null);
  const clients = useRef(new Map<string, MessengerClient>());
  const unsubscribers = useRef(new Map<string, () => void>());
  const serversRef = useRef<ServerEntry[] | null>(null);

  const makeClient = useCallback(
    (serverId: string) => {
      let client = clients.current.get(serverId);
      if (!client) {
        client = clientFactory ? clientFactory(serverId) : new MessengerClient({ platform, serverId });
        clients.current.set(serverId, client);
      }
      return client;
    },
    [clientFactory, platform],
  );

  // Boot: host info and servers.
  const loadServers = useCallback(async () => {
    const list = await platform.listServers().catch(() => [] as ServerEntry[]);
    serversRef.current = list;
    setServers(list);
    return list;
  }, [platform]);

  /** The server reported another company name than the list shows (renamed by the owner): reload the list. */
  const syncCompanyName = useCallback(
    async (serverId: string, companyName: string | undefined) => {
      const entry = serversRef.current?.find((s) => s.serverId === serverId);
      if (!companyName || !entry || entry.companyName === companyName) return;
      await platform.serverRegistry?.refresh(serverId).catch(() => null);
      await loadServers();
    },
    [platform, loadServers],
  );

  /** Forgets a server's session: its client stops (socket, timers, uploads) and its badge goes. */
  const dropClient = useCallback((serverId: string) => {
    const client = clients.current.get(serverId);
    clients.current.delete(serverId);
    unsubscribers.current.get(serverId)?.();
    unsubscribers.current.delete(serverId);
    if (client) {
      client.badgeListener = null;
      client.stop();
    }
    setServerUnread((u) => {
      if (!(serverId in u)) return u;
      const next = { ...u };
      delete next[serverId];
      return next;
    });
  }, []);

  const startClient = useCallback(
    async (serverId: string) => {
      let client: MessengerClient;
      try {
        client = makeClient(serverId);
      } catch {
        setStatuses((s) => ({ ...s, [serverId]: 'error' }));
        return 'error';
      }
      if (client.state.status === 'ready' || client.state.status === 'starting') return client.state.status;
      setStatuses((s) => ({ ...s, [serverId]: 'starting' }));
      const result = await client.start();
      // Replaced or dropped while starting: the newer session owns the status and the listeners.
      if (clients.current.get(serverId) !== client) return result;
      setStatuses((s) => ({ ...s, [serverId]: result }));
      if (result === 'ready') {
        unsubscribers.current.get(serverId)?.();
        const offNotice = client.onNotice((notice) => {
          if (notice.kind !== 'signed_out') return;
          dropClient(serverId);
          setStatuses((s) => ({ ...s, [serverId]: 'signed_out' }));
          toast(SIGNED_OUT_TEXT[notice.reason] ?? SIGNED_OUT_TEXT.expired!);
        });
        client.badgeListener = (count) => setServerUnread((u) => (u[serverId] === count ? u : { ...u, [serverId]: count }));
        setServerUnread((u) => ({ ...u, [serverId]: client.unreadTotal() }));
        const offInfo = client.store.subscribe((state, prev) => {
          if (state.serverInfo !== prev.serverInfo) void syncCompanyName(serverId, state.serverInfo?.companyName);
        });
        void syncCompanyName(serverId, client.state.serverInfo?.companyName);
        unsubscribers.current.set(serverId, () => {
          offNotice();
          offInfo();
        });
      } else if (result === 'signed_out') {
        dropClient(serverId);
      }
      return result;
    },
    [makeClient, dropClient, toast, syncCompanyName],
  );


  useEffect(() => {
    let alive = true;
    void (async () => {
      const info = await platform.appInfo().catch(() => null);
      if (!alive) return;
      if (!info) {
        setBoot((b) => ({ ...b, failed: true }));
        return;
      }
      setAppInfo(info);
      const list = await loadServers();
      const stored = await platform.getActiveServerId().catch(() => null);
      const active = list.find((s) => s.serverId === stored)?.serverId ?? list[0]?.serverId ?? null;
      if (!alive) return;
      setActiveServerId(active);
      if (!active && platform.kind === 'desktop') navigate({ name: 'connect', adding: false });
    })();
    return () => {
      alive = false;
    };
  }, [platform, loadServers, navigate, boot.attempt]);

  // Start sessions: the active one always; on desktop also every other signed-in server (rail badges, notifications).
  const needsSession = route.name === 'messenger' || route.name === 'admin';
  useEffect(() => {
    if (!servers || !activeServerId || !needsSession) return;
    void startClient(activeServerId);
    if (platform.kind === 'desktop') for (const s of servers) if (s.serverId !== activeServerId && s.signedIn) void startClient(s.serverId);
  }, [servers, activeServerId, needsSession, startClient, platform.kind]);

  // Public pages (invite, reset, setup, download) only need the server's public info.
  const publicApi: ApiClient | null = useMemo(() => (activeServerId ? createApiClient(platform.transport(activeServerId)) : null), [platform, activeServerId]);
  useEffect(() => {
    if (!publicApi || needsSession) return;
    void publicApi
      .call(api.meta.serverInfo)
      .then(setPublicInfo)
      .catch(() => setPublicInfo(null));
  }, [publicApi, needsSession]);

  // Dock/taskbar badge across all servers.
  useEffect(() => {
    platform.setBadge(Object.values(serverUnread).reduce((a, b) => a + b, 0));
  }, [platform, serverUnread]);

  // The web keeps drafts, unsent messages and tray files only in this page, so leaving it asks first
  // (the browser's own dialog). Desktop must not: a prevented unload would silently cancel quitting.
  useEffect(() => {
    if (platform.kind !== 'web') return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if ([...clients.current.values()].some((client) => client.hasUnsent())) event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [platform]);

  // An update main verified before this page subscribed (it is announced once).
  useEffect(() => {
    let alive = true;
    void platform.updates
      ?.status()
      .then((found) => {
        if (alive && found) setUpdate(found);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [platform]);

  /**
   * Restarting or quitting ends every session's page, so each server's outbox goes to disk first.
   * Resolves with what goes with the app anyway (null: nothing): without a usable key store (Linux
   * without a keyring, or one that refused a save) nothing reached the disk.
   */
  const saveBeforeQuitting = useCallback(async (): Promise<string | null> => {
    const sessions = [...clients.current.values()];
    await Promise.all(sessions.map((client) => client.saveOutbox()));
    return unsentSummary(sessions.filter((client) => appInfo?.secureStorage !== 'os' || client.state.storage !== 'persistent'));
  }, [appInfo]);

  const installUpdate = useCallback(async () => {
    if (!update || !platform.updates) return;
    if (update.install === 'download' && update.installer?.state !== 'ready') {
      await platform.updates.openDownloadPage();
      return;
    }
    const lost = await saveBeforeQuitting();
    if (lost) {
      const restart = update.install === 'restart';
      const ok = await confirm({
        title: restart ? '지금 재시작할까요?' : '설치 프로그램을 열까요?',
        body: `${restart ? '재시작하면' : '앱이 종료되면'} ${lost}가 사라져요. ${NOT_KEPT}`,
        ok: restart ? '재시작' : '설치',
        danger: true,
      });
      if (!ok) return;
    }
    await platform.updates.restart();
  }, [platform, update, saveBeforeQuitting, confirm]);

  /** "완전히 종료" in settings: always asks (no notifications until the app starts again) and names what would be lost. */
  const quitApp = useCallback(async () => {
    if (!platform.quit) return;
    const lost = await saveBeforeQuitting();
    const ok = await confirm({
      title: '앱을 완전히 종료할까요?',
      body: lost ? `종료하면 다시 실행할 때까지 새 메시지 알림을 받을 수 없고, ${lost}가 사라져요. ${NOT_KEPT}` : '종료하면 다시 실행할 때까지 새 메시지 알림을 받을 수 없어요.',
      ok: '완전히 종료',
      danger: true,
    });
    if (ok) await platform.quit();
  }, [platform, saveBeforeQuitting, confirm]);

  /**
   * The shell asks to quit (the window closed while it does not stay in the background, the tray's
   * 완전히 종료, ⌘Q) where unsent messages cannot be kept: quit at once unless something would be lost.
   */
  const answerQuitRequest = useCallback(async () => {
    if (!platform.quit) return;
    const lost = await saveBeforeQuitting();
    if (lost && !(await confirm({ title: '앱을 완전히 종료할까요?', body: `종료하면 ${lost}가 사라져요. ${NOT_KEPT}`, ok: '완전히 종료', danger: true }))) return;
    await platform.quit();
  }, [platform, saveBeforeQuitting, confirm]);

  // The OS logs out or shuts down, or the app quits some other way: the page goes without asking, so what
  // the outboxes hold is sent to disk now rather than after their debounce. Best effort; desktop only.
  useEffect(() => {
    if (platform.kind !== 'desktop') return;
    const flush = () => {
      for (const client of clients.current.values()) void client.saveOutbox();
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [platform]);

  const switchServer = useCallback(
    (serverId: string) => {
      setActiveServerId(serverId);
      void platform.setActiveServer(serverId).catch(() => {});
      const s = servers?.find((x) => x.serverId === serverId);
      navigate({ name: 'messenger' });
      if (s && statuses[serverId] === 'ready') {
        const prev = servers?.find((x) => x.serverId === activeServerId);
        toast(`${s.companyName} 서버로 전환했어요${prev ? ` · ${prev.companyName} 서버와 계정·알림이 분리돼요` : ''}`);
      }
    },
    [platform, servers, statuses, activeServerId, navigate, toast],
  );

  // Desktop shell events.
  useEffect(
    () =>
      platform.onEvent((event) => {
        switch (event.type) {
          case 'servers.changed':
            void loadServers();
            break;
          case 'deeplink.connect':
            navigate({ name: 'connect', adding: true, initialAddress: event.serverUrl });
            break;
          case 'update.available':
            setUpdate(event.update);
            break;
          case 'update.installer':
            setUpdate((current) => (current?.version === event.version ? { ...current, installer: event.installer } : current));
            break;
          // Halted, expired or no longer listed: nothing of it may be downloaded or opened any more.
          case 'update.withdrawn':
            setUpdate((current) => (current?.version === event.version ? null : current));
            break;
          // Only here does opening the file by hand help: the checked installer is there, the system had no program for it.
          case 'update.openRefused':
            toast('설치 프로그램을 열지 못했어요. 폴더에서 파일을 열어 주세요');
            break;
          case 'app.quitRequested':
            void answerQuitRequest().catch(() => {});
            break;
          case 'navigate':
            if (event.target === 'settings') setModal({ kind: 'settings' });
            else if (event.target === 'connect') navigate({ name: 'connect', adding: true });
            else navigate({ name: 'messenger' });
            break;
          case 'notification.clicked': {
            if (event.serverId !== activeServerId) switchServer(event.serverId);
            navigate({ name: 'messenger' });
            const client = clients.current.get(event.serverId);
            void client?.showConversation(event.conversationId);
            break;
          }
          default:
            break;
        }
      }),
    [platform, loadServers, navigate, activeServerId, switchServer, toast, answerQuitRequest],
  );

  const onConnected = useCallback(
    async ({ server }: { server: ConnectedServer; me: Me }) => {
      dropClient(server.serverId);
      setStatuses((s) => ({ ...s, [server.serverId]: 'idle' }));
      await loadServers();
      setActiveServerId(server.serverId);
      void platform.setActiveServer(server.serverId).catch(() => {});
      navigate({ name: 'messenger' }, { replace: true });
    },
    [dropClient, loadServers, platform, navigate],
  );

  const env: AppEnv | null = useMemo(
    () =>
      appInfo
        ? {
            platform,
            appInfo,
            servers: servers ?? [],
            activeServerId,
            serverUnread,
            switchServer,
            addServer: () => navigate({ name: 'connect', adding: true }),
            navigate: (screen) => navigate({ name: screen }),
            now,
            theme,
            modal,
            openModal: setModal,
            closeModal: () => setModal(null),
            update,
            installUpdate,
            quitApp,
          }
        : null,
    [appInfo, platform, servers, activeServerId, serverUnread, switchServer, navigate, now, theme, modal, update, installUpdate, quitApp],
  );

  const actions = useMemo<AppActions>(() => ({ now, openModal: setModal }), [now]);

  if (boot.failed) {
    return (
      <Centered>
        <Icon name="alertc" size="lg" />
        <b>앱을 시작하지 못했어요</b>
        <span style={{ color: 'var(--ink-2)', fontSize: 14 }}>다시 시도해도 안 되면 앱을 완전히 종료한 뒤 다시 열어 주세요.</span>
        <Button variant="primary" onClick={() => setBoot((b) => ({ attempt: b.attempt + 1, failed: false }))}>
          다시 시도
        </Button>
      </Centered>
    );
  }
  if (!env || servers === null) return <Loading text="불러오는 중…" />;
  const active = servers.find((s) => s.serverId === activeServerId);
  const connected: ConnectedServer | undefined = active ? { serverId: active.serverId, companyName: active.companyName, serverUrl: active.serverUrl } : undefined;

  // Full-page routes that work without a session.
  if (route.name === 'connect') {
    return (
      <AppEnvContext.Provider value={env}>
        <ConnectScreen
          platform={platform}
          mode={platform.kind}
          server={platform.kind === 'web' ? connected : undefined}
          initialAddress={route.initialAddress}
          onConnected={(r) => void onConnected(r)}
          onCancel={route.adding && servers.length ? () => navigate({ name: 'messenger' }) : undefined}
        />
      </AppEnvContext.Provider>
    );
  }
  if (route.name === 'download') {
    return (
      <LazyPage>
        <DownloadPage
          api={publicApi}
          companyName={active?.companyName ?? publicInfo?.companyName ?? ''}
          serverUrl={active?.serverUrl ?? publicInfo?.serverUrl ?? ''}
          onContinueWeb={() => navigate({ name: 'messenger' })}
        />
      </LazyPage>
    );
  }
  if (route.name === 'invite' || route.name === 'reset' || route.name === 'setup') {
    if (!publicApi) return <NoServer />;
    if (route.name === 'setup')
      return (
        <LazyPage>
          <SetupScreen api={publicApi} onDone={() => navigate({ name: 'messenger' }, { replace: true })} />
        </LazyPage>
      );
    if (!publicInfo) return <Loading text="서버 정보를 확인하는 중…" />;
    if (route.name === 'invite')
      return (
        <LazyPage>
          <InviteAcceptScreen
            api={publicApi}
            token={route.token}
            serverInfo={publicInfo}
            onOpenMessenger={() => {
              setStatuses((s) => ({ ...s, [activeServerId ?? '']: 'idle' }));
              if (activeServerId) dropClient(activeServerId);
              navigate({ name: 'messenger' }, { replace: true });
            }}
            onOpenDownload={() => navigate({ name: 'download' })}
            onGoLogin={() => navigate({ name: 'messenger' }, { replace: true })}
          />
        </LazyPage>
      );
    return (
      <LazyPage>
        <PasswordResetScreen api={publicApi} token={route.token} serverInfo={publicInfo} onDone={() => navigate({ name: 'messenger' }, { replace: true })} />
      </LazyPage>
    );
  }

  if (!activeServerId || !connected) {
    if (platform.kind === 'web') {
      // The page is served by the company server, so an empty list means it is unreachable right now.
      return (
        <Centered>
          <Icon name="wifioff" size="lg" />
          <b>회사 서버에 연결할 수 없어요</b>
          <span style={{ color: 'var(--ink-2)', fontSize: 14 }}>네트워크 연결을 확인한 뒤 다시 시도해 주세요.</span>
          <Button variant="primary" onClick={() => void loadServers().then((list) => setActiveServerId(list[0]?.serverId ?? null))}>
            다시 시도
          </Button>
        </Centered>
      );
    }
    return <NoServer onConnect={() => navigate({ name: 'connect', adding: false })} />;
  }

  const status = statuses[activeServerId] ?? 'idle';
  if (status === 'signed_out') {
    return (
      <AppEnvContext.Provider value={env}>
        <ConnectScreen
          platform={platform}
          mode={platform.kind}
          server={connected}
          initialAddress={platform.kind === 'desktop' ? connected.serverUrl : undefined}
          onConnected={(r) => void onConnected(r)}
          onCancel={platform.kind === 'desktop' && servers.length > 1 ? () => switchServer(servers.find((s) => s.serverId !== activeServerId)!.serverId) : undefined}
        />
      </AppEnvContext.Provider>
    );
  }
  if (status === 'error') {
    const code = clients.current.get(activeServerId)?.state.error;
    if (code === 'UNSUPPORTED_CLIENT' && platform.kind === 'web') {
      return (
        <Centered>
          <Icon name="sync" size="lg" />
          <b>회사 서버가 업데이트됐어요</b>
          <span style={{ color: 'var(--ink-2)', fontSize: 14 }}>페이지를 새로고침하면 새 버전으로 이어서 쓸 수 있어요.</span>
          <Button variant="primary" onClick={() => window.location.reload()}>
            새로고침
          </Button>
        </Centered>
      );
    }
    if (code === 'UNSUPPORTED_CLIENT') {
      // The server alone decides that this screen appears, so on desktop the provider's page (main) offers the installer.
      const updates = platform.updates;
      return (
        <Centered>
          <Icon name="download" size="lg" />
          <b>앱을 업데이트해야 해요</b>
          <span style={{ color: 'var(--ink-2)', fontSize: 14 }}>
            {connected.companyName} 서버가 이 앱 버전{appInfo ? `(${appInfo.appVersion})` : ''}을 더 이상 지원하지 않아요. 새 버전을 설치해도 대기 중인 메시지와 임시저장은 그대로 남아요.
          </span>
          {updates ? (
            <Button variant="primary" onClick={() => void updates.openDownloadPage().catch(() => toast('다운로드 페이지를 열지 못했어요'))}>
              새 버전 받기
            </Button>
          ) : (
            <a className="btn primary" href={`${connected.serverUrl}/download`} target="_blank" rel="noreferrer">
              새 버전 받기
            </a>
          )}
        </Centered>
      );
    }
    if (code === 'UNSUPPORTED_SERVER') {
      return (
        <Centered>
          <Icon name="server" size="lg" />
          <b>회사 서버 업데이트가 필요해요</b>
          <span style={{ color: 'var(--ink-2)', fontSize: 14 }}>이 앱은 {connected.companyName} 서버보다 새 버전이에요. 회사 관리자에게 서버 업데이트를 요청하세요.</span>
          <Button onClick={() => void startClient(activeServerId)}>다시 시도</Button>
        </Centered>
      );
    }
    return (
      <Centered>
        <Icon name="wifioff" size="lg" />
        <b>회사 서버에 연결할 수 없어요</b>
        <span style={{ color: 'var(--ink-2)', fontSize: 14 }}>{connected.serverUrl.replace(/^https?:\/\//, '')} · 네트워크 연결을 확인한 뒤 다시 시도해 주세요.</span>
        <Button variant="primary" onClick={() => void startClient(activeServerId)}>
          다시 시도
        </Button>
      </Centered>
    );
  }
  const client = clients.current.get(activeServerId);
  if (status !== 'ready' || !client) return <Loading text={`${connected.companyName}에 연결하는 중…`} />;

  let screen: ReactNode;
  if (route.name === 'admin') {
    screen = (
      <AdminRoute
        client={client}
        onBack={() => navigate({ name: 'messenger' })}
        invite={
          modal?.kind === 'invite' ? (
            <Suspense fallback={null}>
              <InviteModal onClose={() => setModal(null)} />
            </Suspense>
          ) : null
        }
        onOpenInvite={() => setModal({ kind: 'invite' })}
      />
    );
  } else {
    screen = <MessengerScreen initialView={initialView} allowPostureParam={allowPostureParam} />;
  }
  return (
    <AppEnvContext.Provider value={env}>
      <AppActionsContext.Provider value={actions}>
        <ClientContext.Provider value={client} key={activeServerId}>
          <AvatarSourceContext.Provider value={client}>{screen}</AvatarSourceContext.Provider>
        </ClientContext.Provider>
      </AppActionsContext.Provider>
    </AppEnvContext.Provider>
  );
}

/**
 * The admin screen for the signed-in profile as it is now (a role changed elsewhere shows at once).
 * Someone's profile or role changing makes it read its member list again.
 */
function AdminRoute({ client, onBack, onOpenInvite, invite }: { client: MessengerClient; onBack(): void; onOpenInvite(): void; invite: ReactNode }) {
  const me = useClientState((s) => s.me);
  const serverInfo = useClientState((s) => s.serverInfo);
  const [profileUpdates, setProfileUpdates] = useState(0);
  useEffect(
    () =>
      client.onNotice((notice) => {
        if (notice.kind === 'profile.updated') setProfileUpdates((n) => n + 1);
      }),
    [client],
  );
  if (!me || (me.role !== 'owner' && me.role !== 'admin') || !serverInfo) {
    return (
      <Centered>
        <Icon name="shield" size="lg" />
        <b>회사 관리자만 볼 수 있어요</b>
        <Button onClick={onBack}>메신저로 돌아가기</Button>
      </Centered>
    );
  }
  return (
    <>
      <LazyPage>
        <AdminScreen
          api={client.api}
          me={me}
          serverInfo={serverInfo}
          onBack={onBack}
          onOpenInvite={onOpenInvite}
          onRoleChanged={() => void client.refreshMe().catch(() => {})}
          onCompanyChanged={() => void client.refreshServerInfo().catch(() => {})}
          profileUpdates={profileUpdates}
        />
      </LazyPage>
      {invite}
    </>
  );
}

function NoServer({ onConnect }: { onConnect?: () => void }) {
  return (
    <Centered>
      <Icon name="server" size="lg" />
      <b>연결된 회사 서버가 없어요</b>
      {onConnect ? (
        <Button variant="primary" onClick={onConnect}>
          회사 서버 연결
        </Button>
      ) : (
        <span style={{ color: 'var(--ink-2)', fontSize: 14 }}>회사 서버 주소로 다시 접속해 주세요.</span>
      )}
    </Centered>
  );
}
